import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { apiError } from "@/lib/api";
import { EDITORIAL } from "@/data/site";
import { toast } from "sonner";

function AuthShell({ title, kicker, children, image }) {
  return (
    <div className="grid min-h-[calc(100vh-120px)] lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={image} alt="HYEAMAN" className="h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        <p className="absolute bottom-10 left-10 font-serif text-3xl italic text-gold">Wear Confidence</p>
      </div>
      <div className="flex items-center justify-center px-6 py-16 sm:px-12">
        <div className="w-full max-w-sm">
          <p className="overline-label mb-2">{kicker}</p>
          <h1 className="mb-8 font-black uppercase tracking-tight text-cream text-3xl">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="mb-4 block">
      <span className="overline-label mb-1 block text-[0.6rem]">{label}</span>
      <input required type={type} value={value} onChange={onChange} data-testid={testid}
        className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream outline-none transition focus:border-gold" />
    </label>
  );
}

export function Login() {
  const { login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); toast.success("Welcome back"); navigate("/profile"); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign In" kicker="Member Access" image={EDITORIAL.darkPortrait}>
      <form onSubmit={submit} data-testid="login-form">
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} testid="login-email" />
        <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password" />
        {err && <p className="mb-4 text-sm text-red-400" data-testid="login-error">{err}</p>}
        <button disabled={loading} data-testid="login-submit" className="w-full bg-gold py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright disabled:opacity-50">{loading ? "Signing in…" : "Sign In"}</button>
      </form>
      <div className="mt-6 flex justify-between text-xs text-cream/50">
        <Link to="/register" className="hover:text-gold">Create account</Link>
        <Link to="/forgot-password" className="hover:text-gold">Forgot password?</Link>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await register(form.name, form.email, form.password); toast.success("Welcome to HYEAMAN"); navigate("/profile"); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create Account" kicker="Join the Circle" image={EDITORIAL.female}>
      <form onSubmit={submit} data-testid="register-form">
        <Field label="Full name" value={form.name} onChange={set("name")} testid="register-name" />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="register-email" />
        <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="register-password" />
        {err && <p className="mb-4 text-sm text-red-400" data-testid="register-error">{err}</p>}
        <button disabled={loading} data-testid="register-submit" className="w-full bg-gold py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright disabled:opacity-50">{loading ? "Creating…" : "Create Account"}</button>
      </form>
      <p className="mt-6 text-xs text-cream/50">Already a member? <Link to="/login" className="text-gold hover:text-gold-bright">Sign in</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false); const [devToken, setDevToken] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const api = (await import("@/lib/api")).default;
    const { data } = await api.post("/auth/forgot-password", { email });
    setDevToken(data.dev_token || ""); setSent(true);
  };
  return (
    <AuthShell title="Reset Password" kicker="Recover Access" image={EDITORIAL.blackLabel}>
      {sent ? (
        <div data-testid="forgot-sent">
          <p className="text-sm text-cream/70">If an account exists for {email}, a reset link has been generated.</p>
          {devToken && <Link to={`/reset-password?token=${devToken}`} className="mt-4 inline-block text-sm text-gold underline">Dev: continue to reset →</Link>}
          <Link to="/login" className="mt-6 block text-xs uppercase tracking-widest text-cream/50 hover:text-gold">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} testid="forgot-email" />
          <button data-testid="forgot-submit" className="w-full bg-gold py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">Send Reset Link</button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState(""); const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated"); navigate("/login");
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  return (
    <AuthShell title="New Password" kicker="Secure Your Account" image={EDITORIAL.goldJacketMan}>
      <form onSubmit={submit} data-testid="reset-form">
        <Field label="New password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} testid="reset-password" />
        {err && <p className="mb-4 text-sm text-red-400">{err}</p>}
        <button data-testid="reset-submit" className="w-full bg-gold py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">Update Password</button>
      </form>
    </AuthShell>
  );
}
